import React, { CSSProperties, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import {
  CollectionPath,
  SettingCollectionPath,
} from '@typings/collections.enum';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';

import { useRecoilState, useRecoilValue } from 'recoil';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { IBoardSet } from '@typings/menu/boardSetType';
import {
  MENU_TEXT_TYPE,
  MENU_TEXT_TYPE_VALUE,
} from '@typings/menu/menuTextType';
import {
  layoutDirState,
  layoutTypeState,
  menuTextTypeState,
} from '@atom/setting/homepageSet';
import { isMasterState, isMemberState } from '@atom/setting/user';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { boardSetListState } from '@atom/setting/boardSetListState';
import {
  TOOLTIP_DIR_TYPE,
  TOOLTIP_DIR_TYPE_VALUE,
} from '@typings/common/tooltipDirType';
import NavItem from './NavItem';
import NavFolder from './NavFolder';
import { BOARD_TYPE } from '@typings/boardType';
import { motion } from 'framer-motion';
import { LAYOUT_DIR } from '@typings/setting/layoutDir';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import { scrollbarHide } from '@styles/variants/scrollbar';
import {
  SUB_MENU_DIRECTION,
  SUB_MENU_DIRECTION_VALUE,
  SUB_MENU_FLEX_DIRECTION,
  SUB_MENU_FLEX_DIRECTION_VALUE,
} from '@typings/menu/subMenuDirectionType';
import { ACCESS_TYPE, ACCESS_TYPE_VALUE } from '@typings/common/accessType';

// 메뉴 텍스트 정렬 매핑
const menuAlignMap = {
  [MENU_TEXT_TYPE.LEFT]: 'flex-start',
  [MENU_TEXT_TYPE.CENTER]: 'center',
  [MENU_TEXT_TYPE.RIGHT]: 'flex-end',
};

// 레이아웃 타입별 스타일 매핑
const layoutStyleMap = {
  [LAYOUT_TYPE.LEFT]: css`
    flex-direction: column;
    height: 100%;
    width: 100%;
    justify-content: flex-start;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
    flex-direction: column;
    height: auto;
    width: 100%;
    justify-content: flex-start;
  `,
  [LAYOUT_TYPE.TOP]: css`
    flex-direction: row;
    width: 100%;
    height: 100%;
    align-items: center;
  `,
  [LAYOUT_TYPE.MEMO]: css`
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: flex-start;
    ${scrollbarHide}
  `,
  [LAYOUT_TYPE.BLOG]: css`
    flex-direction: column;
    width: 100%;
    justify-content: flex-start;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    flex-direction: column;
    height: 100%;
    width: 100%;
    justify-content: flex-start;
  `,
  [LAYOUT_TYPE.GAMEUI]: css`
    position: fixed;
    top: 50%;
    right: 24px;
    transform: translateY(-50%);

    display: grid;
    grid-template-columns: 1fr 1.5fr;
    height: auto;
    justify-content: flex-start;
    align-items: center;
    padding: 12px;

    @media (max-width: 768px) {
      position: relative;
      top: unset;
      right: unset;
      transform: unset;
      padding: 0;
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
    }
  `,
};

// 레이아웃 타입별 정렬 스타일 매핑
const getAlignmentStyle = (
  align: MENU_TEXT_TYPE_VALUE,
  layoutType: LAYOUT_TYPE_VALUE,
) => {
  if (layoutType === LAYOUT_TYPE.TOP) {
    return `justify-content: ${menuAlignMap[align]};`;
  } else {
    return `align-items: ${menuAlignMap[align]};`;
  }
};

// 전체 네비게이션 컨테이너
const NavContainer = styled.div<{
  layoutType: LAYOUT_TYPE_VALUE;
}>`
  width: 100%;
  height: 100%;

  // TOP 레이아웃에서는 가로 배치
  ${({ layoutType }) =>
    layoutType === LAYOUT_TYPE.TOP &&
    css`
      flex-direction: row;
      align-items: center;
    `}

  ${({ layoutType }) =>
    layoutType === LAYOUT_TYPE.MINIHOME &&
    css`
      height: fit-content;
    `}
`;

const mobileAlignMap: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// 메인 메뉴 래퍼
const MainMenuWrapper = styled(motion.ul) <{
  align: MENU_TEXT_TYPE_VALUE;
  layoutType: LAYOUT_TYPE_VALUE;
  $mobileAlign?: 'left' | 'center' | 'right';
}>`
  display: flex;
  position: relative;
  gap: var(--menuGap, 8px);
  padding: 8px 0;

  // 레이아웃 타입별 기본 스타일 적용
  ${({ layoutType }) => layoutStyleMap[layoutType]}

  // 정렬 스타일 적용
  ${({ align, layoutType }) => getAlignmentStyle(align, layoutType)}

  // 세로 레이아웃에서 중앙/우측 정렬 시:
  // 자식(StyledNavLink, NavFolder)이 width:100%이면 align-items가 시각적으로 동작하지 않음
  // fit-content로 줄여야 실제 정렬이 반영됨
  ${({ align, layoutType }) =>
    layoutType !== LAYOUT_TYPE.TOP &&
    align !== MENU_TEXT_TYPE.LEFT &&
    css`
      > * {
        width: fit-content;
      }
    `}

  img {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    align-items: ${({ $mobileAlign }) =>
      mobileAlignMap[$mobileAlign || 'left']};
    justify-content: ${({ $mobileAlign }) =>
      mobileAlignMap[$mobileAlign || 'left']};

    /* center/right 정렬 시: 자식 width를 fit-content로 줄여야 align-items가 실제로 적용됨
       (NavItem StyledNavLink의 width:100%, NavFolder root div 모두 대상) */
    ${({ $mobileAlign }) =>
      $mobileAlign &&
      $mobileAlign !== 'left' &&
      css`
        > * {
          width: fit-content !important;
        }
      `}
  }
`;

type Props = {
  tooltipDir?: TOOLTIP_DIR_TYPE_VALUE;
  style?: CSSProperties;
  subMenuDirection?: SUB_MENU_DIRECTION_VALUE;
  subMenuFlexDirection?: SUB_MENU_FLEX_DIRECTION_VALUE;
  forceLayoutType?: LAYOUT_TYPE_VALUE;
};

function NavList({ tooltipDir, style, forceLayoutType }: Props) {
  const layoutDir = useRecoilValue(layoutDirState);
  const _layoutType = useRecoilValue(layoutTypeState);
  const layoutType = forceLayoutType ?? _layoutType;
  const isMaster = useRecoilValue(isMasterState);
  const isMember = useRecoilValue(isMemberState);
  const homeSet = useRecoilValue(homeSettingState);
  const boardSetList = useRecoilValue(boardSetListState);
  const [menuTextType, setMenuTextType] =
    useRecoilState<MENU_TEXT_TYPE_VALUE>(menuTextTypeState);

  // 🔍 디버깅: 중앙화된 설정 및 보드리스트 확인
  useEffect(() => {
    if (homeSet) {
      setMenuTextType(homeSet.menu?.menuTextType || MENU_TEXT_TYPE.LEFT);
    }
  }, [homeSet, boardSetList]);

  const hasMenuReadPermission = useMemo(() => {
    return (accessType?: ACCESS_TYPE_VALUE) => {
      switch (accessType) {
        case ACCESS_TYPE.MEMBER:
          return isMaster || isMember;
        case ACCESS_TYPE.GUEST:
          return isMaster || !isMember;
        case ACCESS_TYPE.SECRET:
          return isMaster;
        case ACCESS_TYPE.PUBLIC:
        default:
          return true;
      }
    };
  }, [isMaster, isMember]);

  // 메인 메뉴와 서브 메뉴 분리 (isVisible이 false + 권한 미충족 메뉴 필터링)
  const { mainMenus, subMenus } = useMemo(() => {
    if (!boardSetList) return { mainMenus: [], subMenus: [] };

    // isVisible이 false가 아닌 메뉴 + 접근 권한을 만족하는 메뉴만 표시
    const visibleMenus = boardSetList.filter(
      (item) =>
        item.isVisible !== false && hasMenuReadPermission(item.accessType),
    );

    const mainMenus: IBoardSet[] = visibleMenus;
    const subMenus: IBoardSet[] = [];

    visibleMenus.forEach((item) => {
      if (item.isSubMenu === true) {
        subMenus.push(item);
      }
    });

    return { mainMenus, subMenus };
  }, [boardSetList, hasMenuReadPermission]);

  // 메뉴 아이템 렌더링 함수
  const renderMenuItem = (list: IBoardSet, index: number) => {
    const { folderID } = list;

    if (folderID) return null;

    if (list.type === BOARD_TYPE.FOLDER) {
      // 메뉴타입이 '폴더' 일 때, 해당 폴더 id를 가지고 있는 메뉴 출력 (isVisible 필터 적용)
      const filteredList =
        boardSetList?.filter(
          (item) =>
            item.folderID === list.id &&
            item.isVisible !== false &&
            hasMenuReadPermission(item.accessType),
        ) || [];

      return (
        <NavFolder
          key={list.id}
          data={list}
          subMenuList={filteredList}
          menuItemType={homeSet!.menu.menuItemType}
          menuTextType={menuTextType}
          layoutType={layoutType}
          tooltipDir={
            tooltipDir || layoutDir === LAYOUT_DIR.LEFT
              ? TOOLTIP_DIR_TYPE.RIGHT
              : TOOLTIP_DIR_TYPE.LEFT
          }
        />
      );
    }

    return (
      <NavItem
        key={list.id}
        list={list}
        tooltipDir={
          tooltipDir || layoutDir === LAYOUT_DIR.LEFT
            ? TOOLTIP_DIR_TYPE.RIGHT
            : TOOLTIP_DIR_TYPE.LEFT
        }
        layoutType={layoutType}
        menuTextType={menuTextType}
        menuItemType={homeSet!.menu.menuItemType}
      />
    );
  };

  // 메인 메뉴 리스트
  const mainMenuList = mainMenus.map((list, index) =>
    renderMenuItem(list, index),
  );

  if (!homeSet) return null;

  return boardSetList ? (
    <NavContainer layoutType={layoutType} style={style}>
      {/* 메인 메뉴 렌더링 */}
      <MainMenuWrapper
        align={menuTextType}
        layoutType={layoutType}
        $mobileAlign={(homeSet as any)?.menu?.mobileMenuAlign}
      >
        {mainMenuList}
      </MainMenuWrapper>
    </NavContainer>
  ) : null;
}
export default NavList;
